import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { WhatsappLogo, EnvelopeSimple, MapPin, LinkedinLogo, InstagramLogo } from "@phosphor-icons/react";
import { Reveal, SectionHeading } from "@/components/site/Primitives";
import { FAQS, CONTACT, LOGO } from "@/data/content";

export const Faq = () => (
  <section id="faq" data-testid="faq-section" className="py-24 md:py-32 bg-white">
    <div className="w-full max-w-4xl mx-auto px-6 md:px-12">
      <Reveal>
        <SectionHeading center overline="Answers" title="Frequently Asked Questions" testId="faq" />
      </Reveal>
      <Reveal delay={0.1} className="mt-12">
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger data-testid={`faq-trigger-${i}`}
                className="text-left font-display text-lg md:text-xl text-navy hover:text-gold hover:no-underline py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent data-testid={`faq-content-${i}`} className="text-base text-slate-600 leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const Contact = () => {
  const msg = encodeURIComponent("Hi JP Accounts, I'd like to know more about your services.");
  const waLinks = CONTACT.whatsapp.map((n) => `https://wa.me/${n}?text=${msg}`);
  const mailto = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Enquiry — JP Accounts & Tax Consultants")}`;
  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-cream">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <Reveal>
            <SectionHeading overline="Let's Talk" title="Get in Touch"
              subtitle="Reach out on WhatsApp or Email and we'll get back to you shortly." testId="contact" />

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-11 h-11 rounded-lg bg-navy flex items-center justify-center text-gold shrink-0">
                  <MapPin size={22} weight="duotone" />
                </div>
                <div>
                  <p className="font-semibold text-navy">Address</p>
                  <p className="text-slate-600">{CONTACT.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-whatsapp-numbers">
                <div className="w-11 h-11 rounded-lg bg-navy flex items-center justify-center text-gold shrink-0">
                  <WhatsappLogo size={22} weight="duotone" />
                </div>
                <div>
                  <p className="font-semibold text-navy">WhatsApp</p>
                  {CONTACT.phones.map((p, idx) => (
                    <a key={p} href={waLinks[idx]} target="_blank" rel="noopener noreferrer"
                      className="block text-slate-600 hover:text-gold transition-colors">+91 {p}</a>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-11 h-11 rounded-lg bg-navy flex items-center justify-center text-gold shrink-0">
                  <EnvelopeSimple size={22} weight="duotone" />
                </div>
                <div>
                  <p className="font-semibold text-navy">Email</p>
                  <a href={mailto} className="text-slate-600 hover:text-gold transition-colors">{CONTACT.email}</a>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild data-testid="contact-whatsapp-btn"
                className="rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white font-semibold h-12 px-6 transition-all duration-300">
                <a href={waLinks[0]} target="_blank" rel="noopener noreferrer"><WhatsappLogo size={20} weight="fill" className="mr-2" /> Chat on WhatsApp</a>
              </Button>
              <Button asChild variant="outline" data-testid="contact-email-btn"
                className="rounded-full border-navy/30 text-navy hover:bg-navy hover:text-white font-semibold h-12 px-6 transition-all duration-300">
                <a href={mailto}><EnvelopeSimple size={20} weight="fill" className="mr-2" /> Send Email</a>
              </Button>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="bg-navy rounded-2xl shadow-xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-[0.05]"
                style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "24px 24px" }} />
              <div className="relative">
                <div className="inline-flex w-16 h-16 rounded-full bg-gold/15 items-center justify-center text-gold mx-auto">
                  <WhatsappLogo size={34} weight="fill" />
                </div>
                <h3 className="mt-6 font-display text-2xl md:text-3xl font-semibold text-white">Have a question?</h3>
                <p className="mt-3 text-slate-300 leading-relaxed max-w-sm mx-auto">
                  Message us on WhatsApp for a quick reply, or drop us an email — we're happy to help with your accounting and tax needs.
                </p>
                <div className="mt-8 flex flex-col gap-3 max-w-xs mx-auto">
                  <Button asChild data-testid="contact-cta-whatsapp"
                    className="rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white font-semibold h-12 transition-all duration-300">
                    <a href={waLinks[0]} target="_blank" rel="noopener noreferrer"><WhatsappLogo size={20} weight="fill" className="mr-2" /> Chat on WhatsApp</a>
                  </Button>
                  <Button asChild data-testid="contact-cta-email"
                    className="rounded-full bg-gold hover:bg-gold-hover text-white font-semibold h-12 transition-all duration-300">
                    <a href={mailto}><EnvelopeSimple size={20} weight="fill" className="mr-2" /> Email Us</a>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const wa = `https://wa.me/${CONTACT.whatsapp[0]}`;
  return (
    <footer data-testid="footer" className="bg-navy text-white">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="JP Accounts & Tax Consultants" className="h-12 w-12 rounded-lg object-contain bg-white/5" />
              <span className="font-display text-xl font-bold">JP Accounts & Tax Consultants</span>
            </div>
            <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
              Professional Accounting & Taxation Services for individuals, professionals, startups and businesses across India.
            </p>
          </div>
          <div>
            <p className="font-display text-lg font-semibold text-gold">Quick Links</p>
            <ul className="mt-4 space-y-2.5 text-slate-300">
              <li><a href="#about" data-testid="footer-about" className="hover:text-gold transition-colors">About</a></li>
              <li><a href="#services" data-testid="footer-services" className="hover:text-gold transition-colors">Services</a></li>
              <li><a href="#faq" data-testid="footer-faq" className="hover:text-gold transition-colors">FAQ</a></li>
              <li><a href="#contact" data-testid="footer-contact" className="hover:text-gold transition-colors">Contact</a></li>
            </ul>
          </div>
          <div>
            <p className="font-display text-lg font-semibold text-gold">Connect</p>
            <div className="mt-4 flex gap-3">
              <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gold transition-colors"><LinkedinLogo size={20} weight="fill" /></a>
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gold transition-colors"><InstagramLogo size={20} weight="fill" /></a>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gold transition-colors"><EnvelopeSimple size={20} weight="fill" /></a>
              <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gold transition-colors"><WhatsappLogo size={20} weight="fill" /></a>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 text-center text-sm text-slate-400">
          © 2026 JP Accounts & Tax Consultants. All Rights Reserved.
        </div>
      </div>
    </footer>
  );
};
